/**
 * Snowie at Home Funnel Lead Form
 * - Client-side validation for the lead form
 * - Honeypot + minimum fill time to keep bots out of the endpoint's quota
 * - POST to a Zapier catch hook (form-encoded) or an Odoo automation
 *   webhook (JSON), with field renaming, a key prefix and static extras
 *
 * Transport: a normal cross-origin POST is tried first, so a real HTTP error
 * surfaces to the visitor. Endpoints that send no CORS headers reject that at
 * the preflight, and we retry opaquely with mode: 'no-cors' — which cannot
 * carry a Content-Type header, so the JSON rides as text/plain and the
 * response is unreadable. Success UI is optimistic only on that fallback.
 */

(function () {
  'use strict';

  if (window.__snowieAtHomeLeadFormInit) return;
  window.__snowieAtHomeLeadFormInit = true;

  var MIN_FILL_MS = 2000;
  var UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];
  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  var loadedAt = Date.now();

  function queryParams() {
    var out = {};
    try {
      new URLSearchParams(window.location.search).forEach(function (value, key) {
        out[key] = value;
      });
    } catch (e) {}
    return out;
  }

  function collectUtms() {
    var out = {};
    try {
      if (window.SNOWIE_AT_HOME && typeof window.SNOWIE_AT_HOME.utms === 'function') {
        out = window.SNOWIE_AT_HOME.utms() || {};
      }
    } catch (e) {}

    var q = queryParams();
    UTM_KEYS.forEach(function (key) {
      if (q[key]) out[key] = q[key];
    });
    return out;
  }

  function clearErrors(form) {
    form.querySelectorAll('.has-error').forEach(function (el) {
      el.classList.remove('has-error');
    });
    var box = form.querySelector('[data-lead-error]');
    if (box) {
      box.textContent = '';
      box.classList.remove('is-visible');
    }
  }

  function showError(form, message, field) {
    var box = form.querySelector('[data-lead-error]');
    if (box) {
      box.textContent = message;
      box.classList.add('is-visible');
    }
    if (field) {
      var wrapper = field.closest('.snowie-at-home-lead-form__field, .snowie-at-home-lead-form__consent');
      if (wrapper) wrapper.classList.add('has-error');
      if (typeof field.focus === 'function') field.focus();
    }
  }

  function validate(form) {
    var required = form.querySelectorAll('[data-required]');
    for (var i = 0; i < required.length; i++) {
      if (!required[i].value.trim()) {
        showError(form, 'Please fill in every field.', required[i]);
        return false;
      }
    }

    var email = form.querySelector('input[name="email"]');
    if (email && email.value.trim() && !EMAIL_RE.test(email.value.trim())) {
      showError(form, 'Please enter a valid email address.', email);
      return false;
    }

    var phone = form.querySelector('input[name="phone"]');
    if (phone && phone.value.trim() && phone.value.replace(/\D/g, '').length < 7) {
      showError(form, 'Please enter a valid phone number.', phone);
      return false;
    }

    var consent = form.querySelector('[data-required-consent]');
    if (consent && !consent.checked) {
      showError(form, 'Please accept the privacy policy to continue.', consent);
      return false;
    }

    return true;
  }

  function showSuccessMessage(form) {
    var wrapper = form.closest('.snowie-at-home-lead-form');
    var success = wrapper && wrapper.querySelector('[data-lead-success]');
    form.style.display = 'none';
    if (success) {
      success.classList.add('is-visible');
      success.setAttribute('tabindex', '-1');
      success.focus();
    }
  }

  function cartItems(form) {
    var raw = form.getAttribute('data-cart-items');
    if (!raw) return [];
    try {
      var parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      console.warn('[snowie-at-home] Could not read the lead form cart items.', e);
      return [];
    }
  }

  function clearCart() {
    return fetch('/cart/clear.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' }
    });
  }

  // Items are added one at a time on purpose. A single /cart/add.js call with
  // several items fails as a unit, so one sold-out variant would drop the
  // whole order. Individually, the rest still make it through.
  function addItem(item) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: item.id, quantity: item.quantity || 1 })
    }).then(function (response) {
      if (!response.ok) {
        return response.json().catch(function () { return {}; }).then(function (data) {
          console.warn('[snowie-at-home] Variant ' + item.id + ' could not be added.', data.description || response.status);
          return false;
        });
      }
      return true;
    }).catch(function () {
      return false;
    });
  }

  function fieldValue(form, name) {
    var input = form.querySelector('[name="' + name + '"]');
    return input && input.value ? input.value.trim() : '';
  }

  // Shopify wants first and last name separately; the form collects one field.
  function splitName(full) {
    var parts = full.split(/\s+/).filter(Boolean);
    if (!parts.length) return { first: '', last: '' };
    if (parts.length === 1) return { first: parts[0], last: '' };
    return { first: parts[0], last: parts.slice(1).join(' ') };
  }

  function prefillParams(form) {
    if (form.getAttribute('data-prefill-checkout') !== 'true') return [];

    var params = [];
    var email = fieldValue(form, 'email');
    var phone = fieldValue(form, 'phone');
    var prefix = fieldValue(form, 'phone_prefix');
    var name = splitName(fieldValue(form, 'name'));

    if (email) params.push(['checkout[email]', email]);
    if (name.first) params.push(['checkout[shipping_address][first_name]', name.first]);
    if (name.last) params.push(['checkout[shipping_address][last_name]', name.last]);
    if (phone) params.push(['checkout[shipping_address][phone]', (prefix ? prefix + ' ' : '') + phone]);
    return params;
  }

  function withQuery(path, params) {
    if (!params.length) return path;
    var query = params
      .map(function (pair) {
        return encodeURIComponent(pair[0]) + '=' + encodeURIComponent(pair[1]);
      })
      .join('&');
    return path + (path.indexOf('?') === -1 ? '?' : '&') + query;
  }

  function destinationUrl(form) {
    var action = form.getAttribute('data-success-action');
    var discount = form.getAttribute('data-discount-code');
    var params = prefillParams(form);
    var target = action === 'checkout' ? '/checkout' : '/cart';

    // The code also rides as a query parameter, for checkouts that read it
    // there rather than from the session the fetch below establishes.
    if (discount) params = params.concat([['discount', discount]]);
    return withQuery(target, params);
  }

  // Visited rather than redirected through, so the destination URL stays flat.
  // Nesting a fully-encoded destination inside ?redirect= means the bracket
  // syntax of checkout[email] survives only if Shopify decodes exactly once.
  function applyDiscount(form) {
    var discount = form.getAttribute('data-discount-code');
    if (!discount) return Promise.resolve();
    return fetch('/discount/' + encodeURIComponent(discount), {
      method: 'GET',
      credentials: 'same-origin'
    }).catch(function () {
      // A failure here is not fatal: the code still rides on the query string.
    });
  }

  function sendToCart(form) {
    var items = cartItems(form);
    if (!items.length) {
      applyDiscount(form).then(function () {
        window.location.href = destinationUrl(form);
      });
      return;
    }

    var start = form.getAttribute('data-clear-cart') === 'true'
      ? clearCart().catch(function () {})
      : Promise.resolve();

    start
      .then(function () {
        return items.reduce(function (chain, item) {
          return chain.then(function (added) {
            return addItem(item).then(function (ok) { return added || ok; });
          });
        }, Promise.resolve(false));
      })
      .then(function (anyAdded) {
        if (!anyAdded) {
          // Nothing could be added - sold out, or bad variant ids. Sending the
          // visitor to an empty cart is worse than the thank-you message.
          console.warn('[snowie-at-home] No items could be added to the cart; showing the success message instead.');
          showSuccessMessage(form);
          return null;
        }
        return applyDiscount(form).then(function () {
          window.location.href = destinationUrl(form);
        });
      });
  }

  function succeed(form) {
    var action = form.getAttribute('data-success-action') || 'message';
    var redirect = form.getAttribute('data-redirect');

    try {
      if (window.SNOWIE_AT_HOME && typeof window.SNOWIE_AT_HOME.track === 'function') {
        window.SNOWIE_AT_HOME.track('Lead');
      }
    } catch (e) {}

    if (action === 'cart' || action === 'checkout') {
      sendToCart(form);
      return;
    }

    if (action === 'redirect' && redirect) {
      window.location.href = redirect;
      return;
    }

    // Legacy behaviour: a redirect URL set with no explicit action.
    if (action === 'message' && redirect) {
      window.location.href = redirect;
      return;
    }

    showSuccessMessage(form);
  }

  function parseStaticFields(form) {
    var raw = form.getAttribute('data-static-fields');
    if (!raw) return {};
    try {
      var parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch (e) {
      console.warn('[snowie-at-home] Could not read the lead form static fields.', e);
      return {};
    }
  }

  function fieldNameMap(form) {
    return {
      name: form.getAttribute('data-field-name') || 'name',
      email: form.getAttribute('data-field-email') || 'email',
      phone: form.getAttribute('data-field-phone') || 'phone'
    };
  }

  function buildPayload(form) {
    var names = fieldNameMap(form);
    var prefix = form.getAttribute('data-field-prefix') || '';
    var payload = {};

    function put(key, value) {
      payload[prefix + key] = value;
    }

    new FormData(form).forEach(function (value, key) {
      if (key === 'company_website') return;
      put(names[key] || key, value);
    });

    put('page_url', window.location.href);
    put('page_title', document.title);
    put('referrer', document.referrer || '');
    put('submitted_at', new Date().toISOString());

    var utms = collectUtms();
    Object.keys(utms).forEach(function (key) {
      put(key, utms[key]);
    });

    // Static fields are written verbatim: they are the endpoint's own keys
    // (team_id, source_id, type), so the prefix must not touch them.
    var statics = parseStaticFields(form);
    Object.keys(statics).forEach(function (key) {
      payload[key] = statics[key];
    });

    return payload;
  }

  function encodeForm(payload) {
    var data = new FormData();
    Object.keys(payload).forEach(function (key) {
      data.append(key, payload[key]);
    });
    return data;
  }

  function send(url, payload, asJson) {
    var body = asJson ? JSON.stringify(payload) : encodeForm(payload);
    var options = { method: 'POST', body: body };
    if (asJson) options.headers = { 'Content-Type': 'application/json' };

    // Two-argument then, deliberately: the second handler catches only a
    // rejected fetch — a network or CORS failure, where retrying opaquely is
    // the whole point. A real HTTP error resolves the promise, so the throw
    // below is NOT caught here and surfaces to the visitor. Using .catch()
    // would swallow genuine 4xx/5xx responses and report false success.
    return fetch(url, options).then(
      function (response) {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return response;
      },
      function () {
        // Endpoint sent no CORS headers. A no-cors request may not set
        // Content-Type, so JSON rides as text/plain and the response is
        // opaque — success is assumed from here on.
        return fetch(url, { method: 'POST', mode: 'no-cors', body: body });
      }
    );
  }

  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!form || !form.matches('[data-snowie-at-home-lead-form]')) return;

    event.preventDefault();
    clearErrors(form);

    // Bots fill hidden fields and submit faster than a person can type.
    var trap = form.querySelector('input[name="company_website"]');
    if ((trap && trap.value) || Date.now() - loadedAt < MIN_FILL_MS) {
      succeed(form);
      return;
    }

    if (!validate(form)) return;

    var webhook = form.getAttribute('data-webhook');
    var errorMessage = form.getAttribute('data-error-message') || 'Something went wrong. Please try again.';

    if (!webhook) {
      showError(form, errorMessage);
      console.warn('[snowie-at-home] Lead form has no webhook URL. Set one on the block or in Theme settings > Snowie at Home Funnel.');
      return;
    }

    var button = form.querySelector('button[type="submit"]');
    var loadingText = form.getAttribute('data-loading-text');
    if (button) {
      button.setAttribute('aria-busy', 'true');
      if (loadingText) {
        button.setAttribute('data-original-label', button.textContent);
        button.textContent = loadingText;
      }
    }

    var asJson = form.getAttribute('data-endpoint-type') === 'json';

    send(webhook, buildPayload(form), asJson)
      .then(function () {
        succeed(form);
      })
      .catch(function () {
        if (button) {
          button.removeAttribute('aria-busy');
          var original = button.getAttribute('data-original-label');
          if (original) button.textContent = original;
        }
        showError(form, errorMessage);
      });
  });
})();
